import countries from "i18n-iso-countries";
import englishLocale from "i18n-iso-countries/langs/en.json" with { type: "json" };

countries.registerLocale(englishLocale);

export type Ga4CountryIdentifier = string | number;

export type Ga4CountryRow =
  | Readonly<{
      country: Ga4CountryIdentifier;
      countryCode?: never;
      visitors: number;
    }>
  | Readonly<{
      country?: never;
      countryCode: Ga4CountryIdentifier;
      visitors: number;
    }>;

export interface SupportedCountryAnalytics {
  readonly status: "supported";
  readonly alpha2: string;
  readonly numeric: string;
  readonly name: string;
  readonly visitors: number;
  readonly sourceValues: readonly string[];
}

export interface UnmappedCountryAnalytics {
  readonly status: "unmapped";
  readonly rawValue: string;
  readonly visitors: number;
}

export interface NormalizedGa4Countries {
  readonly supported: readonly SupportedCountryAnalytics[];
  readonly unmapped: readonly UnmappedCountryAnalytics[];
  readonly totalVisitors: number;
  readonly mappedVisitors: number;
  readonly unmappedVisitors: number;
}

export class CountryAnalyticsNormalizationError extends Error {
  readonly reasonCode:
    | "invalid_country_identifier"
    | "ambiguous_country_identifier"
    | "invalid_visitor_count"
    | "visitor_total_overflow";

  constructor(reasonCode: CountryAnalyticsNormalizationError["reasonCode"]) {
    super(reasonCode);
    this.name = "CountryAnalyticsNormalizationError";
    this.reasonCode = reasonCode;
  }
}

const alpha2Codes = Object.freeze(countries.getAlpha2Codes());

function normalizedNameKey(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’‘`]/g, "'")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function buildEnglishNameIndex(): ReadonlyMap<string, string> {
  const candidates = new Map<string, Set<string>>();
  const names = countries.getNames("en", { select: "all" });
  for (const [alpha2, aliases] of Object.entries(names)) {
    for (const alias of aliases) {
      const key = normalizedNameKey(alias);
      if (!key) continue;
      const matches = candidates.get(key) ?? new Set<string>();
      matches.add(alpha2);
      candidates.set(key, matches);
    }
  }
  const unambiguous = new Map<string, string>();
  for (const [key, matches] of candidates) {
    if (matches.size === 1) unambiguous.set(key, [...matches][0]!);
  }
  return unambiguous;
}

const englishNameIndex = buildEnglishNameIndex();

function safeAdd(left: number, right: number): number {
  const total = left + right;
  if (!Number.isSafeInteger(total)) {
    throw new CountryAnalyticsNormalizationError("visitor_total_overflow");
  }
  return total;
}

function validatedVisitors(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new CountryAnalyticsNormalizationError("invalid_visitor_count");
  }
  return value;
}

function rowIdentifier(row: Ga4CountryRow): Ga4CountryIdentifier {
  const hasCountry = Object.hasOwn(row, "country");
  const hasCountryCode = Object.hasOwn(row, "countryCode");
  if (hasCountry === hasCountryCode) {
    throw new CountryAnalyticsNormalizationError(
      hasCountry
        ? "ambiguous_country_identifier"
        : "invalid_country_identifier",
    );
  }
  const identifier = hasCountry ? row.country : row.countryCode;
  if (
    (typeof identifier !== "string" && typeof identifier !== "number") ||
    (typeof identifier === "number" && !Number.isSafeInteger(identifier))
  ) {
    throw new CountryAnalyticsNormalizationError("invalid_country_identifier");
  }
  return identifier;
}

function alpha2For(identifier: Ga4CountryIdentifier): string | null {
  if (typeof identifier === "number") {
    return countries.numericToAlpha2(identifier) ?? null;
  }
  const candidate = identifier.trim();
  if (!candidate) return null;
  if (/^[a-z]{2}$/i.test(candidate)) {
    const alpha2 = candidate.toUpperCase();
    if (Object.hasOwn(alpha2Codes, alpha2)) return alpha2;
  }
  if (/^[a-z]{3}$/i.test(candidate)) {
    const alpha2 = countries.alpha3ToAlpha2(candidate.toUpperCase());
    if (alpha2) return alpha2;
  }
  if (/^\d{1,3}$/.test(candidate)) {
    const alpha2 = countries.numericToAlpha2(Number(candidate));
    if (alpha2) return alpha2;
  }
  return englishNameIndex.get(normalizedNameKey(candidate)) ?? null;
}

export function normalizeGa4Country(
  identifier: Ga4CountryIdentifier,
): Omit<
  SupportedCountryAnalytics,
  "status" | "visitors" | "sourceValues"
> | null {
  const alpha2 = alpha2For(identifier);
  if (!alpha2) return null;
  const numeric = countries.alpha2ToNumeric(alpha2);
  const name = countries.getName(alpha2, "en", { select: "official" });
  if (!numeric || !name) return null;
  return Object.freeze({ alpha2, numeric, name });
}

export function normalizeGa4Countries(
  rows: readonly Ga4CountryRow[],
): NormalizedGa4Countries {
  const supported = new Map<
    string,
    {
      alpha2: string;
      numeric: string;
      name: string;
      visitors: number;
      sourceValues: Set<string>;
    }
  >();
  const unmapped = new Map<string, number>();
  let totalVisitors = 0;

  for (const row of rows) {
    const identifier = rowIdentifier(row);
    const rawValue = String(identifier);
    const visitors = validatedVisitors(row.visitors);
    totalVisitors = safeAdd(totalVisitors, visitors);
    const normalized = normalizeGa4Country(identifier);
    if (!normalized) {
      unmapped.set(rawValue, safeAdd(unmapped.get(rawValue) ?? 0, visitors));
      continue;
    }
    const existing = supported.get(normalized.alpha2);
    if (existing) {
      existing.visitors = safeAdd(existing.visitors, visitors);
      existing.sourceValues.add(rawValue);
    } else {
      supported.set(normalized.alpha2, {
        ...normalized,
        visitors,
        sourceValues: new Set([rawValue]),
      });
    }
  }

  const supportedCountries = [...supported.values()]
    .map((country): SupportedCountryAnalytics =>
      Object.freeze({
        status: "supported",
        alpha2: country.alpha2,
        numeric: country.numeric,
        name: country.name,
        visitors: country.visitors,
        sourceValues: Object.freeze([...country.sourceValues]),
      }),
    )
    .sort(
      (left, right) =>
        right.visitors - left.visitors || left.name.localeCompare(right.name),
    );
  const unmappedCountries = [...unmapped.entries()]
    .map(([rawValue, visitors]): UnmappedCountryAnalytics =>
      Object.freeze({ status: "unmapped", rawValue, visitors }),
    )
    .sort(
      (left, right) =>
        right.visitors - left.visitors ||
        left.rawValue.localeCompare(right.rawValue),
    );
  const mappedVisitors = supportedCountries.reduce(
    (total, country) => safeAdd(total, country.visitors),
    0,
  );
  const unmappedVisitors = unmappedCountries.reduce(
    (total, country) => safeAdd(total, country.visitors),
    0,
  );
  if (safeAdd(mappedVisitors, unmappedVisitors) !== totalVisitors) {
    throw new CountryAnalyticsNormalizationError("visitor_total_overflow");
  }

  return Object.freeze({
    supported: Object.freeze(supportedCountries),
    unmapped: Object.freeze(unmappedCountries),
    totalVisitors,
    mappedVisitors,
    unmappedVisitors,
  });
}
